import React, { useState, useEffect } from 'react';
import { Plus, Activity, AlertCircle, CheckCircle, Clock, Trash2, Info } from 'lucide-react';
import * as api from '../services/api';
import { Lightbulb } from 'lucide-react';
import { LightbulbOff } from 'lucide-react';

const Dashboard = ({ apps, lighthouses, onSelectApp, onSelectLighthouse, onRefreshApps, onRefreshLighthouses, workerApiUrl, user }) => {
  const [showAddAppModal, setShowAddAppModal] = useState(false);
  const [newAppName, setNewAppName] = useState('');
  const [newAppTtl, setNewAppTtl] = useState('0'); // Default: never expires
  const [recentEvents, setRecentEvents] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [showAddlighthouseModal, setShowAddlighthouseModal] = useState(false);
  const [newlighthouseName, setNewlighthouseName] = useState('');
  const [newlighthouseUrl, setNewlighthouseUrl] = useState('');
  const [isCreatinglighthouse, setIsCreatinglighthouse] = useState(false);
  const [newlighthouseInterval, setNewlighthouseInterval] = useState('3');
  const [showAllLighthouses, setShowAllLighthouses] = useState(false);
  const [appLimit, setAppLimit] = useState(0); // New state for app limit

  // Extract app limit from user's plan features
  useEffect(() => {
    if (user && user.plan && user.plan.features) {
      const appFeature = user.plan.features.find(feature => feature.toLowerCase().includes('app'));
      if (appFeature) {
        const match = appFeature.match(/(\d+)\s*app/i);
        if (match && match[1]) {
          setAppLimit(parseInt(match[1], 10));
        } else if (appFeature.toLowerCase().includes('unlimited')) {
          setAppLimit(Infinity); // Set to a very high number or a special value for unlimited
        }
      }
    } else {
      setAppLimit(0); // Default to 0 if no plan or features
    }
  }, [user]);

  // Fetch recent events from all apps
  useEffect(() => {
    const fetchRecentEvents = async () => {
      try {
        // Fetch logs from all apps and combine them
        const allEvents = [];
        for (const app of apps) {
          try {
            const data = await api.getLogs(workerApiUrl, {
              appId: app.id,
              limit: 5
            });
            const eventsWithAppName = data.logs.map(log => ({
              ...log,
              appName: app.name,
              appId: app.id
            }));
            allEvents.push(...eventsWithAppName);
          } catch (err) {
            console.error(`Failed to fetch logs for app ${app.name}:`, err);
          }
        }
        
        // Sort by timestamp and take the most recent 10
        allEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setRecentEvents(allEvents.slice(0, 10));
      } catch (err) {
        console.error('Failed to fetch recent events:', err);
      }
    };

    if (apps.length > 0) {
      fetchRecentEvents();
    }
  }, [apps, workerApiUrl]);

  const handleCreateApp = async (e) => {
    e.preventDefault();
    if (!newAppName.trim()) return;

    setIsCreating(true);
    try {
      const newApp = await api.createApp(workerApiUrl, newAppName.trim(), parseInt(newAppTtl, 10));
      console.log('App created:', newApp);
      setNewAppName('');
      setNewAppTtl('0');
      setShowAddAppModal(false);
      onRefreshApps();
    } catch (err) {
      alert(`Failed to create app: ${err.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateLighthouse = async (e) => {
    e.preventDefault();
    if (!newlighthouseName.trim()) return;

    setIsCreatinglighthouse(true);
    try {
      const newLighthouse = await api.createLighthouse(
        workerApiUrl, 
        {
          name: newlighthouseName.trim(), 
          url: newlighthouseUrl.trim(),
          interval: newlighthouseInterval.trim()
        }
      );
      console.log('Lighthouse created:', newLighthouse);
      setNewlighthouseName('');
      setShowAddlighthouseModal(false);
      onRefreshLighthouses();
    } catch (err) {
      alert(`Failed to create lighthouse: ${err.message}`);
    } finally {
      setIsCreatinglighthouse(false);
    }
  }; 

  const handleDeleteLighthouse = async (id, e) => {
    e.stopPropagation(); // Prevent card click
    if (window.confirm('Are you sure you want to delete this lighthouse?')) {
      try {
        await api.deleteLighthouse(workerApiUrl, id);
        onRefreshLighthouses();
      } catch (err) {
        alert(`Failed to delete lighthouse: ${err.message}`);
      }
    }
  };

  const getStatusColor = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return 'healthy';
    if (statusCode >= 400 && statusCode < 500) return 'failed';
    if (statusCode >= 500) return 'failed';
    return 'pending';
  };

  const getStatusText = (statusCode) => {
    if (statusCode === null || statusCode === undefined) return 'Pending';
    if (statusCode >= 200 && statusCode < 300) return 'Healthy';
    return 'Failed';
  }


  const getStatusIcon = (type, status) => {
   
    switch (status) {
      case 'operational':
        if (type === 'lighthouse') {
          return <Lightbulb size={20} className="status-icon-operational" />;
        }
        return <CheckCircle size={20} className="status-icon-operational" />;
      case 'degraded':
        if (type === 'lighthouse') {
          return <Lightbulb size={20} className="status-icon-degraded" />;
        }
        return <AlertCircle size={20} className="status-icon-degraded" />;
      case 'down':
        if (type === 'lighthouse') {
          return <LightbulbOff size={20} className="status-icon-down" />;
        }
        return <AlertCircle size={20} className="status-icon-down" />;
      default:
        return <Activity size={20} />;
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        
      </div>

      {/* Lighthouses Overview Section */}
      <section className="dashboard-section">
        <div className="section-header-controls">
          <h2>Lighthouses</h2>
          
            <button onClick={() => onSelectLighthouse("")} className="btn-secondary btn-small">
              {showAllLighthouses ? 'Show Less' : 'View More'}
            </button>
          
        </div>
        <div className={`lighthouse-list-container ${!showAllLighthouses && lighthouses.length > 3 ? 'collapsed' : ''}`}>
          {lighthouses.length > 0 ? (
            <div className="lighthouse-list">
              {(showAllLighthouses ? lighthouses : lighthouses.slice(0, 3)).map(lighthouse => {
                const status = getStatusColor(lighthouse.last_status_code);
                return (
                  <div key={lighthouse.id} className="lighthouse-list-item">
                    <div className="lighthouse-info" >
                      <span className={`status-dot ${status}`}></span>
                      <div className="lighthouse-details">
                        <span className="lighthouse-name">{lighthouse.name}</span>
                        <span className="lighthouse-url">{lighthouse.url}</span>
                      </div>
                    </div>
                    <div className="lighthouse-status">
                      Status: {getStatusText(lighthouse.last_status_code)}
                    </div>
                    <div className="lighthouse-status-icon">
                      {getStatusIcon('lighthouse',status === 'healthy' ? 'operational' : status === 'failed' ? 'degraded' : 'down')}
                    </div>
                    
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <Lightbulb size={48} />
              <p>No lighthouses yet</p>
              <button className="btn-secondary" onClick={() => setShowAddlighthouseModal(true)}>
                Create your first lighthouse
              </button>
            </div>
          )}
        </div>
         { lighthouses.length === 0 && <div className="empty-state-small empty-state-boarder">
        
        <Lightbulb size={28} />
        <button className="btn-secondary" onClick={() => setShowAddlighthouseModal(true)}>
          + Add lighthouse
        </button>
      </div>}
      </section>


      {/* Apps Overview Section */}
      <section className="dashboard-section">
        <h2>Applications</h2>
        <div className="apps-grid">
          {apps.length > 0 ? (
            <>
            
            {  apps.map(app => {
                const status = getStatusColor(app);
                return (
                  <div 
                    key={app.id} 
                    className="app-card dashboard-app-card"
                    onClick={() => onSelectApp(app)}
                  >
                    <div className="app-card-header">
                      <h3>{app.name}</h3>
                      {getStatusIcon('app',status)}
                    </div>
                    <div className="app-card-status">
                      <span className={`status-badge status-${status}`}>
                        {status}
                      </span>
                    </div>
                    <div className="app-card-meta">
                      <small>Created {new Date(app.created_at).toLocaleDateString()}</small>
                    </div>
                  </div>
                );
              })}
              <div className="empty-state-small empty-state-boarder">
                <Activity size={28} />
                <button 
                  className="btn-secondary" 
                  onClick={() => setShowAddAppModal(true)}
                  disabled={apps.length >= appLimit && appLimit !== Infinity}
                >
                  + Add app
                </button>
              </div>
            </>
            
            
          ) 
          : (
            <div className="empty-state">
              <Activity size={48} />
              <p>No applications yet</p>
              <button 
                className="btn-secondary" 
                onClick={() => setShowAddAppModal(true)}
                disabled={apps.length >= appLimit && appLimit !== Infinity}
              >
                Create your first app
              </button>
            </div>
          )}
        </div>
        {appLimit !== Infinity && apps.length >= appLimit && (
          <div className="app-limit-message">
            <Info size={18} />
            <p>You have reached your app limit ({appLimit}). Upgrade your plan to create more apps.</p>
          </div>
        )}
      </section>

      {/* Recent Events Section */}
      <section className="dashboard-section">
        <h2>Recent Events</h2>
        <div className="recent-events">
          {recentEvents.length > 0 ? (
            <table className="events-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Application</th>
                  <th>Level</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {recentEvents.map((event, idx) => (
                  <tr key={`${event.id}-${idx}`}>
                    <td>
                      <div className="event-time">
                        <Clock size={14} />
                        {formatTimestamp(event.timestamp)}
                      </div>
                    </td>
                    <td>
                      <span className="event-app-name">{event.appName}</span>
                    </td>
                    <td>
                      <span className={`badge level-${event.level?.toLowerCase()}`}>
                        {event.level}
                      </span>
                    </td>
                    <td className="event-message">{event.message || 'No message'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state-small">
              <p>No recent events</p>
            </div>
          )}
        </div>
      </section>


      {/* Add Lighthouse Modal */}
      {/* Add App Modal */}
      {showAddlighthouseModal && (
        <div className="modal-overlay" onClick={() => setShowAddlighthouseModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Application</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowAddlighthouseModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateLighthouse} className="create-lighthouse-form">
              <div className="form-group">
                <label htmlFor="lighthouseName">Lighthouse Name</label>
                <input
                  id="lighthouseName"
                  type="text"
                  value={newlighthouseName}
                  onChange={(e) => setNewlighthouseName(e.target.value)}
                  placeholder="Enter lighthouse name"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="lighthouseurl">Lighthouse url</label>
                <input
                  id="lighthouseurl"
                  type="url"
                  value={newlighthouseUrl}
                  onChange={(e) => setNewlighthouseUrl(e.target.value)}
                  placeholder="Enter lighthouse url"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="lighthouseinterval">Lighthouse interval</label>
                <select
                  id="lighthouseinterval"
                  value={newlighthouseInterval}
                  onChange={(e) => setNewlighthouseInterval(e.target.value)}
                  required
                  autoFocus
                >
                  <option value="3">3 minutes</option>
                  <option value="5">5 minutes</option>
                  <option value="10">10 minutes</option>
                </select>
              </div>
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowAddlighthouseModal(false)}
                  disabled={isCreatinglighthouse}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isCreatinglighthouse || !newlighthouseName.trim()}
                >
                  {isCreatinglighthouse ? 'Creating...' : 'Create Lighthouse'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add App Modal */}
      {showAddAppModal && (
        <div className="modal-overlay" onClick={() => setShowAddAppModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Application</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowAddAppModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateApp}>
              <div className="form-group">
                <label htmlFor="appName">Application Name</label>
                <input
                  id="appName"
                  type="text"
                  value={newAppName}
                  onChange={(e) => setNewAppName(e.target.value)}
                  placeholder="Enter application name"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="appTtl">Token Expiration (TTL)</label>
                <select
                  id="appTtl"
                  value={newAppTtl}
                  onChange={(e) => setNewAppTtl(e.target.value)}
                >
                  <option value="0">Never expires</option>
                  <option value="86400">24 hours</option>
                  <option value="604800">7 days</option>
                  <option value="2592000">30 days</option>
                  <option value="7776000">90 days</option>
                  <option value="31536000">1 year</option>
                </select>
                <small>The initial app token will automatically expire after this period</small>
              </div>
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowAddAppModal(false)}
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isCreating || !newAppName.trim() || (apps.length >= appLimit && appLimit !== Infinity)}
                >
                  {isCreating ? 'Creating...' : 'Create Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

